/**
 * ConditionsPanel — Phase C1/C2 capture and disposal surface.
 *
 * Where a decision's or a commitment's conditions are written down, read, and
 * moved. Three write paths, all of them RPCs (Build Contract §2.3a):
 *
 *   attach_condition             record a new typed claim
 *   record_condition_observation report a reading — NEVER changes status
 *   set_condition_status         the human judgment: holding / in question /
 *                                broken / retired, always with a basis
 *
 * The separation between the second and third is the point of the whole
 * feature. The system can notice that a reported number fell outside a stated
 * bound; it cannot conclude that the assumption is dead, and it does not offer
 * a control that pretends otherwise. Every status control here asks for a
 * basis first, and the strip above it shows breach and status as two separate
 * facts when they disagree.
 *
 * Free-text reconsideration conditions (migration 464) are untouched by this
 * panel. Where one exists it is offered as a starting point for a typed
 * condition, and the original text stays exactly where it was.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Callout, Field, Modal, Panel, Select, TextArea, TextInput } from '../../ds';
import { Button } from '../../ui/Button';
import { ReasonModal } from '../../reversals/ReasonModal';
import ConditionHealthStrip from './ConditionHealthStrip';
import { SignalCaptureModal } from '../../signals/SignalCaptureModal';
import {
  CONDITION_KIND_LABELS,
  CONDITION_STATUS_LABELS,
  canTransitionCondition,
  toConditionInput,
  type ConditionKind,
  type ConditionStatus,
  type ThresholdComparator,
} from '../../../lib/conditionObjects';
import {
  attachCommitmentCondition,
  attachDecisionCondition,
  listConditionsForCommitment,
  listConditionsForDecision,
  recordConditionObservation,
  setConditionStatus,
  type ConditionHealthRow,
} from '../../../lib/conditionObjectsService';

export type ConditionsSubject =
  | { kind: 'decision'; decisionId: string }
  | { kind: 'commitment'; planCycleId: string; itemId: string };

export interface ConditionsPanelProps {
  subject: ConditionsSubject;
  /** Names the thing in the readout's sentence. */
  subjectLabel?: string;
  /** The mig-464 free text, offered as a starting point. Never modified. */
  existingFreeText?: string | null;
  /**
   * Enables the "Record a signal about this" onward link when a condition is
   * marked broken. Omitted when the caller has no workspace context to give
   * the signal (the broken-condition flow itself still works either way —
   * this only gates the follow-on capture).
   */
  workspaceId?: string | null;
  /** Read-only mode for viewers without write access. */
  readOnly?: boolean;
  className?: string;
}

const KIND_OPTIONS = (Object.keys(CONDITION_KIND_LABELS) as ConditionKind[]).map((kind) => ({
  value: kind,
  label: CONDITION_KIND_LABELS[kind],
}));

const COMPARATOR_OPTIONS: { value: ThresholdComparator; label: string }[] = [
  { value: 'gte', label: 'at or above' },
  { value: 'gt', label: 'above' },
  { value: 'lte', label: 'at or below' },
  { value: 'lt', label: 'below' },
  { value: 'eq', label: 'exactly' },
  { value: 'ne', label: 'anything other than' },
];

const KIND_HINT: Record<ConditionKind, string> = {
  assumption: 'Something believed true that nobody is measuring. Recorded so it can be re-checked.',
  threshold: 'A number with a stated bound and a source. This is the kind the watcher can read.',
  dependency: 'This commitment holds only while another item does. Names the item; the relationship engine still owns what the edge means.',
  external_signal: 'A signal or watch outside the plan that would change the picture.',
};

export default function ConditionsPanel({
  subject,
  subjectLabel,
  existingFreeText,
  workspaceId,
  readOnly = false,
  className = '',
}: ConditionsPanelProps) {
  const [rows, setRows] = useState<ConditionHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  // Break 3: a condition marked broken gets the same onward link a tripped
  // term gets — "Record a signal about this", pre-filled.
  const [signalPromptFor, setSignalPromptFor] = useState<ConditionHealthRow | null>(null);
  // The two acts that need a stated basis ask for it in a dialog of the
  // app's own, not the browser's prompt box (owner, 2026-10-07).
  const [pendingMove, setPendingMove] = useState<{ row: ConditionHealthRow; next: ConditionStatus } | null>(null);
  const [pendingReading, setPendingReading] = useState<ConditionHealthRow | null>(null);
  const [readingValue, setReadingValue] = useState('');
  const [readingBasis, setReadingBasis] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [kind, setKind] = useState<ConditionKind>('assumption');
  const [statement, setStatement] = useState('');
  const [comparator, setComparator] = useState<ThresholdComparator>('gte');
  const [boundValue, setBoundValue] = useState('');
  const [unit, setUnit] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [dependsOnItemId, setDependsOnItemId] = useState('');
  const [signalOccurrence, setSignalOccurrence] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const result =
      subject.kind === 'decision'
        ? await listConditionsForDecision(subject.decisionId)
        : await listConditionsForCommitment(subject.planCycleId, subject.itemId);
    setRows(result.rows);
    setError(result.error ?? null);
    setLoading(false);
  }, [subject]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setKind('assumption');
    setStatement('');
    setComparator('gte');
    setBoundValue('');
    setUnit('');
    setSourceLabel('');
    setDependsOnItemId('');
    setSignalOccurrence('');
  };

  async function submit() {
    if (!statement.trim()) {
      setError('State the condition in a sentence someone else could check.');
      return;
    }
    if (kind === 'threshold' && !Number.isFinite(Number(boundValue))) {
      setError('A threshold needs a number to compare against.');
      return;
    }
    if (kind === 'dependency' && !dependsOnItemId.trim()) {
      setError('A dependency condition must name the item it depends on.');
      return;
    }
    if (kind === 'external_signal' && !signalOccurrence.trim()) {
      setError('An external-signal condition must name the signal it tracks.');
      return;
    }

    setBusy(true);
    setError(null);
    const shared = {
      kind,
      statement: statement.trim(),
      threshold:
        kind === 'threshold'
          ? {
              comparator,
              value: Number(boundValue),
              unit: unit.trim() || undefined,
              source_label: sourceLabel.trim() || undefined,
            }
          : undefined,
      signalRef:
        kind === 'external_signal' ? { occurrence: signalOccurrence.trim() } : undefined,
      dependsOnItemId: kind === 'dependency' ? dependsOnItemId.trim() : undefined,
    };
    const result =
      subject.kind === 'decision'
        ? await attachDecisionCondition({ ...shared, decisionId: subject.decisionId })
        : await attachCommitmentCondition({
            ...shared,
            planCycleId: subject.planCycleId,
            itemId: subject.itemId,
          });
    setBusy(false);

    if (!result.ok) {
      // The database's refusals are already written in user language.
      setError(result.error ?? 'The condition was not recorded.');
      return;
    }
    resetForm();
    setAdding(false);
    await load();
  }

  async function move(row: ConditionHealthRow, next: ConditionStatus, basis: string) {
    if (!basis.trim()) {
      setDialogError('A basis is required: say what changed.');
      return;
    }
    setBusy(true);
    const result = await setConditionStatus(row.id, next, basis.trim());
    setBusy(false);
    if (!result.ok) {
      setDialogError(result.error ?? 'The condition was not moved.');
      return;
    }
    setPendingMove(null);
    if (next === 'broken' && workspaceId) {
      setSignalPromptFor(row);
    }
    await load();
  }

  async function observe(row: ConditionHealthRow) {
    const value = Number(readingValue);
    if (!readingValue.trim() || !Number.isFinite(value)) {
      setDialogError('A reading has to be a number.');
      return;
    }
    if (!readingBasis.trim()) {
      setDialogError('A basis is required: say where the reading came from.');
      return;
    }
    setBusy(true);
    const result = await recordConditionObservation(row.id, value, readingBasis.trim());
    setBusy(false);
    if (!result.ok) {
      setDialogError(result.error ?? 'The reading was not recorded.');
      return;
    }
    setPendingReading(null);
    await load();
  }

  function openReading(row: ConditionHealthRow) {
    setReadingValue('');
    setReadingBasis('');
    setDialogError(null);
    setPendingReading(row);
  }

  function openMove(row: ConditionHealthRow, next: ConditionStatus) {
    setDialogError(null);
    setPendingMove({ row, next });
  }

  return (
    <Panel
      label="Conditions"
      title="What this rests on"
      count={rows.length}
      className={className}
      actions={
        !readOnly && (
          <button
            type="button"
            onClick={() => setAdding((open) => !open)}
            className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Record a condition
          </button>
        )
      }
    >
      {error && (
        <Callout tone="coral" label="Not recorded" className="mb-3">
          {error}
        </Callout>
      )}

      {existingFreeText?.trim() && (
        <Callout tone="slate" label="Already on the record" className="mb-3">
          <p className="text-sm">{existingFreeText}</p>
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            This text stays exactly where it is. Recording typed conditions adds to it — nothing
            here rewrites or clears it.
          </p>
        </Callout>
      )}

      {adding && !readOnly && (
        <div className="mb-4 border border-[var(--rule)] p-4">
          <div className="grid gap-3">
            <Field label="Kind" hint={KIND_HINT[kind]}>
              <Select
                value={kind}
                options={KIND_OPTIONS}
                onChange={(e) => setKind(e.target.value as ConditionKind)}
              />
            </Field>

            <Field
              label="Statement"
              required
              hint="Plain words someone else could check without asking you."
            >
              <TextArea
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder="Enterprise-Europe retention stays at or above 94%"
              />
            </Field>

            {kind === 'threshold' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Holds while">
                  <Select
                    value={comparator}
                    options={COMPARATOR_OPTIONS}
                    onChange={(e) => setComparator(e.target.value as ThresholdComparator)}
                  />
                </Field>
                <Field label="Value" required>
                  <TextInput
                    value={boundValue}
                    inputMode="decimal"
                    onChange={(e) => setBoundValue(e.target.value)}
                    placeholder="94"
                  />
                </Field>
                <Field label="Unit" hint="Shown beside every reading.">
                  <TextInput value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="%" />
                </Field>
                <Field label="Source" hint="Where the number will come from.">
                  <TextInput
                    value={sourceLabel}
                    onChange={(e) => setSourceLabel(e.target.value)}
                    placeholder="Enterprise-Europe retention report"
                  />
                </Field>
              </div>
            )}

            {kind === 'dependency' && (
              <Field label="Depends on item" required hint="The item this commitment's validity rests on.">
                <TextInput
                  value={dependsOnItemId}
                  onChange={(e) => setDependsOnItemId(e.target.value)}
                  placeholder="item id"
                />
              </Field>
            )}

            {kind === 'external_signal' && (
              <Field label="Signal" required hint="The occurrence this condition tracks.">
                <TextInput
                  value={signalOccurrence}
                  onChange={(e) => setSignalOccurrence(e.target.value)}
                  placeholder="competitor_launch"
                />
              </Field>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void submit()}
                className="border border-[var(--rule)] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-pri)] disabled:opacity-50"
              >
                {busy ? 'Recording…' : 'Record'}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setAdding(false);
                  setError(null);
                }}
                className="px-3 py-1.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-ter)]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[var(--text-ter)]">Loading conditions…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--text-ter)]">
          Nothing is recorded yet. A condition written down is one the record can watch; one left in
          someone&rsquo;s head is not.
        </p>
      ) : (
        <>
          <ConditionHealthStrip rows={rows} subjectLabel={subjectLabel} bare />
          {!readOnly && (
            <ul className="mt-4 list-none border-t border-[var(--rule)] pt-3">
              {rows.map((row) => {
                const condition = toConditionInput(row);
                const moves: ConditionStatus[] = (
                  ['in_question', 'holding', 'broken', 'retired'] as ConditionStatus[]
                ).filter((next) => canTransitionCondition(condition.status, next));
                if (moves.length === 0 && condition.kind !== 'threshold') return null;
                return (
                  <li key={row.id} className="flex flex-wrap items-center gap-2 py-1.5">
                    <span className="mr-1 min-w-0 flex-1 truncate text-xs text-[var(--text-ter)]">
                      {row.statement}
                    </span>
                    {condition.kind === 'threshold' && condition.status !== 'retired' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openReading(row)}
                        className="border border-[var(--rule)] px-2 py-0.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-sec)] disabled:opacity-50"
                      >
                        Report a reading
                      </button>
                    )}
                    {moves.map((next) => (
                      <button
                        key={next}
                        type="button"
                        disabled={busy}
                        onClick={() => openMove(row, next)}
                        className="border border-[var(--rule)] px-2 py-0.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-sec)] disabled:opacity-50"
                      >
                        {CONDITION_STATUS_LABELS[next]}
                      </button>
                    ))}
                    {condition.status === 'broken' && workspaceId && (
                      <button
                        type="button"
                        onClick={() => setSignalPromptFor(row)}
                        className="border border-[var(--coral)] px-2 py-0.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--coral)]"
                      >
                        Record a signal about this
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      {pendingMove && (
        <ReasonModal
          open
          onClose={() => { if (!busy) setPendingMove(null); }}
          title={`Move to ${CONDITION_STATUS_LABELS[pendingMove.next].toLowerCase()}`}
          explain={
            <>
              <span className="font-medium text-[var(--text-pri)]">“{pendingMove.row.statement}”</span>
              {' '}is moving to {CONDITION_STATUS_LABELS[pendingMove.next].toLowerCase()}. Nothing here changes on its own: say what changed.
            </>
          }
          confirmLabel={`Move to ${CONDITION_STATUS_LABELS[pendingMove.next].toLowerCase()}`}
          confirmVariant="primary"
          reasonLabel="What changed (required)"
          reasonPlaceholder="What was learned, from where, and when."
          busy={busy}
          errorMessage={dialogError}
          onConfirm={(basis) => void move(pendingMove.row, pendingMove.next, basis)}
        />
      )}
      {pendingReading && (
        <Modal
          open
          onClose={() => { if (!busy) setPendingReading(null); }}
          title="Report a reading"
          description={pendingReading.statement}
          size="sm"
          dismissible={!busy}
          footer={
            <>
              <Button type="button" variant="secondary" size="sm" onClick={() => setPendingReading(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void observe(pendingReading)}
                disabled={busy || !readingValue.trim() || !readingBasis.trim()}
              >
                {busy ? 'Working…' : 'Record the reading'}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label="Reading" htmlFor="condition-reading-value">
              <TextInput
                id="condition-reading-value"
                type="number"
                inputMode="decimal"
                value={readingValue}
                onChange={(e) => setReadingValue(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </Field>
            <Field label="Where this reading comes from (required)" htmlFor="condition-reading-basis">
              <TextArea
                id="condition-reading-basis"
                value={readingBasis}
                onChange={(e) => setReadingBasis(e.target.value)}
                placeholder="The report, the person, or the system it was read from."
                rows={3}
                disabled={busy}
              />
            </Field>
            {dialogError && <Callout tone="coral">{dialogError}</Callout>}
          </div>
        </Modal>
      )}
      {signalPromptFor && workspaceId && (
        <SignalCaptureModal
          workspaceId={workspaceId}
          planCycleId={subject.kind === 'commitment' ? subject.planCycleId : undefined}
          prefill={{
            freeText: signalPromptFor.statement,
            message: `Condition broke: ${signalPromptFor.statement}`,
            confirmed: true,
            severity: 'high',
          }}
          onClose={() => setSignalPromptFor(null)}
          onRecorded={() => setSignalPromptFor(null)}
        />
      )}
    </Panel>
  );
}
